/**
 * @voltxampere/spice — Capacitor codec (C)
 *
 * Berkeley SPICE3 syntax: `C{name} N+ N- value [IC=v0]`
 *
 * Examples:
 *   C1 out 0 100n
 *   C2 a b 4.7u
 *   Cload net1 net2 1u IC=0V
 *
 * Schema mapping: `type: "capacitor"`, params: `{ capacitance,
 * initialVoltage? }`. The `IC=` clause is parsed when present;
 * Sprint 16 emits it back when `initialVoltage` is non-zero.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { ok, err, } from "./_base.js";
import { parseSpiceNumber, formatSpiceValue } from "../units.js";
import { numericParam } from "./_param-helpers.js";
export const capacitorCodec = {
    spiceLetter: "C",
    schemaTypes: ["capacitor"],
    examples: ["C1 out 0 100n", "C2 a b 4.7u", "Cload net1 net2 1u IC=0V"],
    parse(card, _ctx) {
        if (card.tokens.length < 3) {
            return err("C needs 2 nodes and a value");
        }
        const [na, nb, raw, ...extras] = card.tokens;
        if (na === undefined || nb === undefined || raw === undefined) {
            return err("C has incomplete tokens");
        }
        const value = parseSpiceNumber(raw);
        if (value === null) {
            return err(`C value '${raw}' is not a valid number`);
        }
        let initialVoltage;
        for (const extra of extras) {
            const m = /^IC=(.+)$/i.exec(extra);
            if (m?.[1] !== undefined) {
                const ic = parseSpiceNumber(m[1]);
                if (ic !== null)
                    initialVoltage = ic;
            }
        }
        const params = { capacitance: value };
        if (initialVoltage !== undefined)
            params.initialVoltage = initialVoltage;
        const component = {
            id: card.identifier,
            type: "capacitor",
            position: { x: 0, y: 0 },
            rotation: 0,
            flipped: false,
            label: card.identifier,
            params,
            terminals: [],
        };
        return ok(component, [na, nb]);
    },
    emit(component, terminals) {
        const c = numericParam(component.params.capacitance, 1e-9);
        const ic = numericParam(component.params.initialVoltage, 0);
        const [a, b] = terminals;
        const base = `${component.id} ${a ?? "0"} ${b ?? "0"} ${formatSpiceValue(c)}`;
        if (ic !== 0)
            return `${base} IC=${formatSpiceValue(ic)}`;
        return base;
    },
    owns(schemaType) {
        return schemaType === "capacitor";
    },
};
//# sourceMappingURL=capacitor.js.map